/**
 * Subscription Check Service
 * -------------------------
 * This service provides functions to check the user's subscription status
 * by reading from local storage (userData).
 * 
 * Uses the date-parsing.js module for handling date operations.
 */

import { parseDate, isDateExpired, formatDate } from './date-parsing.js';
import * as localStorageService from './local-storage-service';

// Debug toggle: set to false to disable console logs in production
const DEBUG = false;
function dbgLog(...args) { if (DEBUG) console.log(...args); }
function dbgWarn(...args) { if (DEBUG) console.warn(...args); }

// Subscription types considered premium
export const PREMIUM_TYPES = [
  'month',
  'year',
  'monthly',
  'yearly',
  'premium'
];

/**
 * Get the current subscription type for the user.
 * Checks userData in local storage.
 * @returns {string} The subscription type or 'free plan' if not found.
 */
export function getSubscriptionType() {
  let type = 'free plan';
  const userData = localStorageService.getUserData();
  
  // Check userData fields
  if (userData) {
    if (userData.stripeSubscriptionType) {
      type = userData.stripeSubscriptionType;
    } else if (userData.subscriptionType) {
      type = userData.subscriptionType;
    } else if (userData.subscription && userData.subscription.type) {
      type = userData.subscription.type;
    }
  }
  
  return type;
}

/**
 * Check if the user has a premium subscription.
 * @returns {boolean} True if subscription type is premium, false otherwise.
 */
export function isPremiumSubscription() {
  dbgLog('[SubscriptionCheck] Checking premium status');
  
  const userData = localStorageService.getUserData();
  
  // Determine subscription type
  let type =
    userData?.stripeSubscriptionType ||
    userData?.subscriptionType ||
    userData?.subscription?.type;
  
  dbgLog(`[SubscriptionCheck] Found subscription type: ${type}`);
  
  // First check if the subscription type is premium at all
  const isTypePremium = PREMIUM_TYPES.includes(type);
  if (!isTypePremium) {
    dbgLog('[SubscriptionCheck] Not a premium subscription type, returning false');
    return false;
  }
  
  // Then validate expiration date (only if subscription type is premium)
  const validToStr = userData?.currentPlanValidTo;
  dbgLog(`[SubscriptionCheck] Found expiration date: ${validToStr}`);
  
  if (validToStr) {
    try {
      // Use the date-parsing module to check if the subscription has expired
      const expired = isDateExpired(validToStr);
      
      // Format the date for logging
      const formattedExpiry = formatDate(validToStr);
      const formattedNow = formatDate(new Date());
      
      dbgLog(`[SubscriptionCheck] Comparing dates - Expiry: ${formattedExpiry}, Now: ${formattedNow}`);
      dbgLog(`[SubscriptionCheck] Is expired? ${expired}`);
      
      if (expired) {
        dbgLog('[SubscriptionCheck] Subscription expired, returning false');
        return false;
      }
    } catch (error) {
      dbgWarn('[SubscriptionCheck] Error checking expiration:', error);
      // If there's an error parsing the date, continue with subscription check
      // rather than blocking the user
    }
  }
  
  // If we got here, the type is premium and the subscription is not expired
  dbgLog('[SubscriptionCheck] Subscription is valid and premium, returning true');
  return true;
}

/**
 * Check if the user is on the free plan.
 * @returns {boolean} True if free plan, false otherwise.
 */
export function isFreePlan() {
  return !isPremiumSubscription();
}

// Export as default object for easier importing
const subscriptionCheckService = {
  PREMIUM_TYPES,
  getSubscriptionType,
  isPremiumSubscription,
  isFreePlan
};

export default subscriptionCheckService;
